import React, { useState } from "react";
import { telecmiApi } from "../lib/api";
import { useLiveCall } from "../contexts/LiveCallContext";
import { toast } from "sonner";
import { Sparkles, PhoneCall, Loader2 } from "lucide-react";

/**
 * MockCallStarterCard
 *
 * Lets an admin spin up a pretend live call session so they can preview
 * the LiveCallWidget + AI coaching while Telecmi auth is still broken.
 * Generates a short bilingual mock script and feeds it through the
 * append-transcript API so the experience matches what real Whisper
 * streaming will deliver.
 */
const SCRIPTS = {
  refund_request: [
    { speaker: "customer", text: "Hello, my order EXO-12345 arrived broken. The bronze statue's hand is chipped." },
    { speaker: "agent", text: "I'm so sorry to hear that. Could you share a photo on WhatsApp so we can process this quickly?" },
    { speaker: "customer", text: "I already sent two photos yesterday. Nobody replied. I want a refund, not a replacement." },
    { speaker: "customer", text: "This is the second time I've had to call." },
    { speaker: "agent", text: "I completely understand your frustration. Let me check the photos right now." },
  ],
  upsell_jewelry: [
    { speaker: "customer", text: "I bought the silver Krishna pendant last month. It's beautiful." },
    { speaker: "agent", text: "Thank you! Is there anything else you're looking for?" },
    { speaker: "customer", text: "My daughter's birthday is next week. Maybe earrings to match?" },
    { speaker: "customer", text: "Budget around five thousand rupees." },
  ],
  shipping_query: [
    { speaker: "customer", text: "I'm in Bangalore. How long for delivery to my pincode 560001?" },
    { speaker: "agent", text: "Within India we ship in 3 to 5 business days." },
    { speaker: "customer", text: "Express available? I need it before Friday." },
  ],
};

const MockCallStarterCard = () => {
  const { startCall } = useLiveCall();
  const [phone, setPhone] = useState("919876549999");
  const [name, setName] = useState("Test Customer");
  const [scenario, setScenario] = useState("refund_request");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!phone) { toast.error("Enter a phone"); return; }
    setBusy(true);
    try {
      // Step 1: start a live mock session
      const r = await telecmiApi.liveStart({
        phone,
        direction: "inbound",
        mock: true,
      });
      const callId = r.data.id;
      startCall({ id: callId, phone, name: name || phone });

      // Step 2: drip-feed the transcript over ~12s so coaching loops can fire
      const turns = SCRIPTS[scenario] || SCRIPTS.refund_request;
      let i = 0;
      const interval = setInterval(async () => {
        if (i >= turns.length) { clearInterval(interval); return; }
        const t = turns[i++];
        try {
          await telecmiApi.liveAppend(callId, t);
        } catch (e) {
          clearInterval(interval);
        }
      }, 2500);

      toast.success("Mock call started — widget appears on the right");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start mock");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="mock-call-starter-card">
      <div className="flex items-center gap-2 mb-3">
        <PhoneCall className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-bold">Live-call demo (mock)</h3>
        <span className="text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full uppercase font-bold">Demo / testing</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Simulates an inbound customer call so you can preview the floating
        live-call widget — transcript + AI coaching from Claude appear in
        real time. The mock auto-feeds a short script over ~12s.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="block text-[10px] font-bold uppercase text-gray-500 mb-0.5">Phone</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full text-[12px] px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            data-testid="mock-call-phone"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase text-gray-500 mb-0.5">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full text-[12px] px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            data-testid="mock-call-name"
          />
        </div>
      </div>

      <label className="block text-[10px] font-bold uppercase text-gray-500 mb-0.5">Scenario</label>
      <select
        value={scenario}
        onChange={(e) => setScenario(e.target.value)}
        className="w-full text-[12px] px-2 py-1.5 border border-gray-200 rounded-lg mb-3"
        data-testid="mock-call-scenario"
      >
        <option value="refund_request">Refund request (broken statue)</option>
        <option value="upsell_jewelry">Upsell opportunity (jewelry)</option>
        <option value="shipping_query">Shipping query (Bangalore)</option>
      </select>

      <button
        onClick={start}
        disabled={busy}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[12px] font-bold py-2 rounded-lg flex items-center justify-center gap-1.5"
        data-testid="mock-call-start-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Start mock call
      </button>
    </div>
  );
};

export default MockCallStarterCard;
